import { Plus, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MAX_PORTS, buttonConflicts, freePortCount, newAttachment } from "@/rc/attachments";
import { portConflicts } from "@/rc/drivetrains";
import { AttachmentCard } from "./AttachmentCard";

const Problems = ({ ports, buttons }) => {
  const lines = [
    ports.size > 0 && `Port ${[...ports].join(", ")} is picked for more than one motor.`,
    buttons.size > 0 && `${[...buttons].join(", ")} is used for more than one job.`,
  ].filter(Boolean);
  if (!lines.length) return null;
  return (
    <p role="alert" className="sticky top-0 z-10 flex items-center gap-2 rounded-xl border border-destructive/30 bg-background px-4 py-3 text-sm font-medium text-destructive shadow-sm" data-testid="rc-attachment-conflict">
      <TriangleAlert className="h-4 w-4 shrink-0" />{lines.join(" ")} Each motor needs its own port and each button one job.
    </p>
  );
};

export const AttachmentsStep = ({ cfg, setCfg }) => {
  const free = freePortCount(cfg);
  const ports = portConflicts(cfg);
  const buttons = buttonConflicts(cfg);
  const setAll = (fn) => setCfg((c) => ({ ...c, attachments: fn(c.attachments || []) }));
  const add = (size) => setCfg((c) => ({ ...c, attachments: [...(c.attachments || []), newAttachment(c, size)] }));

  return (
    <div className="space-y-4" data-testid="rc-attachments-step">
      <Problems ports={ports} buttons={buttons} />
      <div className="flex flex-col gap-4 rounded-2xl border bg-muted/30 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-display font-bold">Optional: claws, lifts and arms</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Use 1 motor, or a group of 2 motors that move the same thing. <span className="font-semibold text-foreground" data-testid="rc-free-ports">{free} of {MAX_PORTS} ports free.</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" disabled={free < 1} onClick={() => add(1)} data-testid="rc-add-attachment-1"><Plus className="mr-1.5 h-4 w-4" />1 motor</Button>
          <Button variant="outline" className="rounded-full" disabled={free < 2} onClick={() => add(2)} data-testid="rc-add-attachment-2"><Plus className="mr-1.5 h-4 w-4" />2-motor group</Button>
        </div>
      </div>
      {cfg.attachments.length === 0 && (
        <p className="rounded-2xl border border-dashed px-5 py-8 text-center text-sm text-muted-foreground" data-testid="rc-no-attachments">
          No attachments, and that's fine. Press Next to finish, or add one above.
        </p>
      )}
      {cfg.attachments.map((a, i) => (
        <AttachmentCard
          key={i}
          a={a}
          index={i}
          portConflicts={ports}
          buttonConflicts={buttons}
          onChange={(patch) => setAll((list) => list.map((x, j) => (j === i ? { ...x, ...patch } : x)))}
          onRemove={() => setAll((list) => list.filter((_, j) => j !== i))}
        />
      ))}
    </div>
  );
};
